import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';
import { UserService } from '@niko-crm/ui';
import { UserActions } from './user.actions';
import { extractError } from '../helpers/error-reader';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(UserService);

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUsers),
      switchMap(({ page, limit, search }) =>
        this.userService.getUsers(page, limit, search).pipe(
          map((result) => UserActions.loadUsersSuccess({ result })),
          catchError((err) =>
            of(UserActions.loadUsersFailure({ error: extractError(err) }))
          ),
        )
      ),
    )
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.createUser),
      switchMap(({ payload }) =>
        this.userService.createUser(payload).pipe(
          map((user) => UserActions.createUserSuccess({ user })),
          catchError((err) =>
            of(UserActions.createUserFailure({ error: extractError(err) }))
          ),
        )
      ),
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUser),
      switchMap(({ id, payload }) =>
        this.userService.updateUser(id, payload).pipe(
          map((user) => UserActions.updateUserSuccess({ user })),
          catchError((err) =>
            of(UserActions.updateUserFailure({ error: extractError(err) }))
          ),
        )
      ),
    )
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteUser),
      switchMap(({ id }) =>
        this.userService.deleteUser(id).pipe(
          map((user) => UserActions.deleteUserSuccess({ user })),
          catchError((err) =>
            of(UserActions.deleteUserFailure({ error: extractError(err) }))
          ),
        )
      ),
    )
  );

  reactivateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.reactivateUser),
      switchMap(({ id }) =>
        this.userService.reactivateUser(id).pipe(
          map((user) => UserActions.reactivateUserSuccess({ user })),
          catchError((err) =>
            of(UserActions.reactivateUserFailure({ error: extractError(err) }))
          ),
        )
      ),
    )
  );

  resendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.resendInvitation),
      switchMap(({ id }) =>
        this.userService.resendInvitation(id).pipe(
          map((user) => UserActions.resendInvitationSuccess({ user })),
          catchError((err) =>
            of(UserActions.resendInvitationFailure({ error: extractError(err) }))
          ),
        )
      ),
    )
  );

  resetUserPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.resetUserPassword),
      switchMap(({ id }) =>
        this.userService.resetPassword(id).pipe(
          map(({ user, temporaryPassword }) =>
            UserActions.resetUserPasswordSuccess({ user, temporaryPassword })
          ),
          catchError((err) =>
            of(UserActions.resetUserPasswordFailure({ error: extractError(err) }))
          ),
        )
      ),
    )
  );

  loadMyTenants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadMyTenants),
      switchMap(() =>
        this.userService.getMyTenants().pipe(
          map((tenants) => UserActions.loadMyTenantsSuccess({ tenants })),
          catchError((err) =>
            of(UserActions.loadMyTenantsFailure({ error: extractError(err) }))
          ),
        )
      ),
    )
  );
}
